import { Box, Card, Flex, Grid, Text } from "@radix-ui/themes";
import { type ReactNode, useState } from "react";
import { Navigate } from "react-router-dom";
import BillingHelpCard from "../../components/billing/BillingHelpCard";
import BillingManageCard from "../../components/billing/BillingManageCard";
import ProPricePreview from "../../components/billing/ProPricePreview";
import SubscriptionCard, { type BillingAccessSummary } from "../../components/billing/SubscriptionCard";
import TeamPlanRow from "../../components/billing/TeamPlanRow";
import { useUpgradeAccountCheck } from "../../components/billing/UpgradeAccountCheck";
import DashboardShell from "../../components/dashboard/DashboardShell";
import { CURRENT_PLAN_BADGE_STYLE, HIGHLIGHT_CARD_STYLE, PLAN_CARD_STYLE, PRICE_STYLE, STAT_STYLE } from "../../components/dashboard/tokens";
import LoadingIndicator from "../../components/shared/LoadingIndicator";
import { useUsage } from "../../contexts/UsageContext";
import { getCheckoutUrl, getSubscriptionUrl } from "../../utils/billingUrls";
import { formatResetDate } from "../../utils/usageMonths";

const OTHER_FREE_FEATURES = [
    "ChatGPT, Claude and any MCP client",
    "Community support",
];
const PRO_FEATURES = [
    "Unlimited tool calls",
    "Priority support",
];

function PlanCard({
    name,
    price,
    priceNote,
    features,
    isCurrent,
    highlighted,
    bulletColor,
    action,
}: {
    name: string;
    price: ReactNode;
    priceNote?: string;
    features: string[];
    isCurrent: boolean;
    highlighted?: boolean;
    bulletColor: string;
    action?: ReactNode;
}) {
    return (
        <Card size="3" style={highlighted ? HIGHLIGHT_CARD_STYLE : PLAN_CARD_STYLE}>
            <Flex
                direction="column"
                gap="4"
                style={{
                    height: "100%",
                }}
            >
                <Flex align="center" justify="between">
                    <Text size="4" style={STAT_STYLE}>
                        {name}
                    </Text>
                    {isCurrent && <span style={CURRENT_PLAN_BADGE_STYLE}>Current plan</span>}
                </Flex>

                <Box>
                    {typeof price === "string" ? (
                        <Flex align="baseline" gap="2">
                            <Text size="8" style={PRICE_STYLE}>
                                {price}
                            </Text>
                            <Text
                                size="2"
                                style={{
                                    color: "var(--dc-muted-2)",
                                }}
                            >
                                / month
                            </Text>
                        </Flex>
                    ) : (
                        price
                    )}
                    {priceNote && (
                        <Text as="p" size="2" color="gray" mt="2">
                            {priceNote}
                        </Text>
                    )}
                </Box>

                <Flex direction="column" gap="3">
                    {features.map((feature) => (
                        <Flex key={feature} align="start" gap="3">
                            <span
                                aria-hidden="true"
                                style={{
                                    color: bulletColor,
                                }}
                            >
                                ✓
                            </span>
                            <Text size="2" color={highlighted ? undefined : "gray"}>
                                {feature}
                            </Text>
                        </Flex>
                    ))}
                </Flex>

                {/* mt=auto keeps the action pinned to the bottom so both cards line up. */}
                {action && (
                    <Box
                        style={{
                            marginTop: "auto",
                        }}
                    >
                        {action}
                    </Box>
                )}
            </Flex>
        </Card>
    );
}

const PlanBillingPage = () => {
    const { usage, loading, checkoutStarted, setCheckoutStarted, refresh, billingVisible, conversionContext } = useUsage();
    // The plan card loads the subscription; the Billing and Billing questions cards below read it from here.
    const [subscription, setSubscription] = useState<BillingAccessSummary | null>(null);
    const accountCheck = useUpgradeAccountCheck("billing_page");

    if (loading) {
        return (
            <DashboardShell title="Plan & billing">
                <LoadingIndicator text="Loading plan…" />
            </DashboardShell>
        );
    }

    // Reachable by URL even with the nav item hidden — same rule applies:
    // gate off/shadow and no Pro access means nothing here
    // to show or manage.
    if (!billingVisible) {
        return <Navigate to="/" replace />;
    }

    const isPro = usage?.plan === "pro";
    // The real free-tier limit, not a hardcoded guess — this is exactly the
    // number the rest of this page already shows in the subtitle/usage
    // widget for a free viewer. A Pro viewer's own usage state has no limit
    // to read (callsIncluded is null when unlimited), so there's nothing
    // accurate to show them here — the generic fallback avoids repeating the
    // same wrong-number bug with a different hardcoded value.
    const freeFeatures = [
        usage && !isPro && usage.callsIncluded !== null
            ? `${usage.callsIncluded.toLocaleString()} tool calls per month`
            : "A monthly tool-call limit",
        ...OTHER_FREE_FEATURES,
    ];
    const subtitle = usage
        ? isPro
            ? "You are on Pro. Unlimited tool calls."
            : usage.callsIncluded !== null
              ? `You are on the Free plan. ${Math.max(0, usage.callsIncluded - usage.callsUsed).toLocaleString()} tool calls left this month, resets on ${formatResetDate(usage.resetsAt)}.`
              : "You are on the Free plan."
        : undefined;

    return (
        <DashboardShell title="Plan & billing" subtitle={subtitle}>
            <Grid
                columns={{
                    initial: "1",
                    sm: "2",
                }}
                gap="4"
            >
                <PlanCard
                    name="Free"
                    price="$0"
                    features={freeFeatures}
                    isCurrent={Boolean(usage) && !isPro}
                    bulletColor="var(--dc-muted-2)"
                    action={
                        Boolean(usage) &&
                        !isPro && (
                            <Text
                                as="div"
                                size="2"
                                align="center"
                                style={{
                                    padding: "12px",
                                    color: "var(--dc-muted-2)",
                                }}
                            >
                                Your current plan
                            </Text>
                        )
                    }
                />
                <PlanCard
                    name="Pro"
                    price={<ProPricePreview />}
                    features={PRO_FEATURES}
                    isCurrent={isPro}
                    highlighted
                    bulletColor="var(--dc-success)"
                    action={
                        <SubscriptionCard
                            checkoutUrl={getCheckoutUrl("billing_page")}
                            upgradeSurface="billing_page"
                            conversionContext={conversionContext}
                            subscriptionUrl={getSubscriptionUrl()}
                            externallyLocked={checkoutStarted}
                            onCheckoutStateChange={setCheckoutStarted}
                            onBeforeCheckout={accountCheck.holdCheckout}
                            onSubscriptionChange={(nextSubscription) => {
                                setSubscription(nextSubscription);
                                // Keep the sidebar/usage/badges in sync with what this card just
                                // learned — without this, an upgrade shows "PRO active" here while
                                // every other "is Pro" surface on the page stays stale.
                                if (usage && (usage.plan === "pro") !== nextSubscription.hasProAccess) {
                                    refresh();
                                }
                            }}
                        />
                    }
                />
            </Grid>
            {accountCheck.dialog}
            <TeamPlanRow />
            <BillingManageCard subscription={subscription} />
            <BillingHelpCard subscription={subscription} />
        </DashboardShell>
    );
};

export default PlanBillingPage;
